import { useQuery } from "react-query";
import { useAtomValue } from "jotai";
import { groupBy } from "lodash";
import { useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import {
  daysmartRinkConfig,
  fetchUpcomingSlots,
  TimeSlot,
  useAllBookedMeetings,
} from "./api";
import {
  activeSelectionAtom,
  daysmartAuthStateAtom,
  findRink,
} from "../vendor";
import { Section } from "../components";
import { dayToStringShort, dateToShortTime, useCurrentDate } from "../lib";
import { LoadingDots } from "../LoadingDots";
import { Z_INDEXES } from "../constants";
import { BookButton } from "./BookButton";

export function ScheduleDaysmart() {
  const selection = useAtomValue(activeSelectionAtom);
  const authState = useAtomValue(daysmartAuthStateAtom);
  const rink = selection ? findRink(selection.rinkId) : undefined;
  const daysmartRink = rink ? daysmartRinkConfig(rink) : null;

  const { data, status, isFetching } = useQuery(
    ["multiTimeSlot", "daysmart", daysmartRink?.id, authState?.customerId],
    () => fetchUpcomingSlots(daysmartRink!, authState!),
    { enabled: !!daysmartRink && !!authState }
  );
  const { data: bookedData, isFetching: bookedIsFetching } =
    useAllBookedMeetings();
  const TO_SHOW = 6;
  const [showNum, setShowNum] = useState(TO_SHOW);
  const [expandedSlots, setExpandedSlots] = useState<TimeSlot[] | null>(null);
  const slots = data || [];
  const bookedKeys = bookedData ?? [];

  const expandedDate = expandedSlots?.[0]?.dateOnly;
  const headerString = expandedDate
    ? `← viewing ${dayToStringShort[expandedDate.getDay()]} ${expandedDate.getDate()}`
    : "upcoming schedule";

  const header = (
    <h2
      className="text-text-secondary w-full text-lg mb-4 cursor-pointer"
      onClick={() => setExpandedSlots(null)}
    >
      {headerString}
      {(isFetching || bookedIsFetching) && <LoadingDots />}
    </h2>
  );

  if (status !== "success") {
    return (
      <Section>
        {header}
        <div>loading...</div>
      </Section>
    );
  }

  if (expandedSlots) {
    return (
      <Section>
        {header}
        <DaySlotSingle slots={expandedSlots} bookedKeys={bookedKeys} />
      </Section>
    );
  }

  const slotsByDay = groupBy(slots, (slot) => slot.dateOnly.toDateString());
  return (
    <Section>
      {header}
      <div>
        <div className="w-full grid grid-cols-2 gap-4 max-w-sm">
          {Object.keys(slotsByDay)
            .slice(0, showNum)
            .map((date) => (
              <DayCard
                key={date}
                slots={slotsByDay[date]}
                bookedKeys={bookedKeys}
                onClick={() => setExpandedSlots(slotsByDay[date])}
              />
            ))}
        </div>
        {showNum < Object.keys(slotsByDay).length && (
          <div className="flex flex-row justify-center">
            <button
              className="mt-5 text-text-secondary text-sm underline decoration-dotted"
              onClick={() => setShowNum((n) => n + TO_SHOW)}
            >
              show more
            </button>
          </div>
        )}
      </div>
    </Section>
  );
}

function DaySlotSingle(props: { slots: TimeSlot[]; bookedKeys: string[] }) {
  const now = useCurrentDate(60_000);
  return (
    <div className="bg-bg-brand-alt text-text-on-brand-alt p-6 w-full rounded-md">
      <div
        className="text-xl justify-center items-center"
        style={{
          display: "grid",
          gridTemplateColumns: "4fr 2fr 2fr",
          rowGap: "20px",
          columnGap: "4px",
        }}
      >
        {props.slots.map((slot) => (
          <div className="contents" key={slot.dateTime.toString()}>
            <span className="col-start-1 text-2xl relative">
              {props.bookedKeys.includes(slot.meetingKey) && (
                <span className="absolute -left-5 top-1/2 -translate-y-1/2">
                  <CheckIcon color="success" fontSize="small" />
                </span>
              )}
              <BookButton
                slot={slot}
                smaller
                inactive={
                  props.bookedKeys.includes(slot.meetingKey) ||
                  slot.dateTime < now
                }
              />
            </span>
            <span className="col-start-2">{slot.durationMins}min</span>
            <span className="col-start-3 justify-self-end">
              {slot.currentEnrollment}⛸️
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DayCard(props: {
  slots: TimeSlot[];
  bookedKeys: string[];
  onClick: () => void;
}) {
  const date = props.slots[0].dateOnly;
  const dateStr = `${dayToStringShort[date.getDay()]} ${date.getDate()}`;
  return (
    <div className="relative">
      <div
        className={`h-full bg-bg-brand-alt text-text-on-brand-alt p-5 relative rounded-md cursor-pointer ${Z_INDEXES.DAY_SLOT}`}
        onClick={props.onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            props.onClick();
          }
        }}
      >
        <div className="text-lg xs:text-xl font-normal">{dateStr}</div>
        {props.slots.map((slot) => (
          <div
            key={slot.dateTime.toString()}
            className="text-base xs:text-lg text-justify flex flex-row"
          >
            <span className="flex-auto flex flex-row items-center mr-4">
              {props.bookedKeys.includes(slot.meetingKey) && (
                <div className="absolute -ml-[18px] -mt-[3px]">
                  <CheckIcon color="success" fontSize="inherit" />
                </div>
              )}
              {dateToShortTime(slot.dateTime)}
              {slot.durationMins > 60 && "*"}
            </span>
            <span className="flex-auto flex flex-row-reverse">
              {slot.currentEnrollment}⛸️
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
